var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * THE AI PANEL IS A STACK OF PLACES, DRAWN AS COLUMNS.
 *
 * A reader in a thread opens the canvas, steps into an agent, and opens one of ITS agents. That is
 * a trail — thread → canvas → agent → agent — and it used to be shown one place at a time, so each
 * step covered the one before it and the shape of the hierarchy lived only in the back button.
 *
 * Now the top of the trail is drawn side by side: the place being worked in on the LEFT, and the
 * places it was reached through beside it, up to `COLUMNS` of them. The left column is the specific,
 * dense one; the columns to its right are the bigger picture, at lower density. Opening something
 * from any column pushes it onto the trail and it slides in on the left; closing the left column is
 * stepping back; closing a column to the right removes that one step of history. The agent tree is
 * the way to jump without pushing. Nothing here is a second kind of state — the columns ARE the
 * trail, which is why closing one restores exactly the view that was there before it.
 *
 * The rail widens by one column's width per column and is anchored to the right edge, so a column
 * arriving on the left is revealed where it belongs and nothing already on screen moves; leaving,
 * the rail narrows and the column goes the way it came.
 *
 * Each column is a `<vitesse-ai-surface>` — a thread, an agent's record or the canvas — that knows
 * its own place and asks this panel, through a small `nav`, whenever it wants to go somewhere.
 * This element owns the trail, the recents hub, the new-thread composer and the connection strip;
 * it holds no conversation of its own.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { lastConversation, rememberConversation } from './session-memory';
import { emptyTrail, visitOrdered, back as backTrail, canBack as canBackTrail, current as currentPlace, replace as replacePlace, window as windowTrail, pop as popTrail, closeAt as closeAtTrail, } from './nav-trail';
import { artifactOf, placeKey } from './places';
import { t } from '../i18n';
import './composer';
import './hub';
import './surface';
import { headLead } from './head-lead';
import { badgeMetric, statusBadge } from '../components/templates';
import { button, linkButton } from '../components/base-buttons';
import { conversationLabel } from './conversation-list';
import { runLabel } from './run-format';
import '../elements/vitesse-button';
import '../elements/vitesse-icon';
import '../elements/vitesse-resizable-column';
import { RAIL_STORAGE_KEY, RAIL_MAX_W } from '../elements/rail-resize';
/** What the main content keeps, whatever the stack beside it wants. Mirrors the rail's own floor —
 *  the rail clamps its width to it, and this is what decides how many columns fit inside that. */
const MIN_CONTENT_W = 360;
/*
 * A COLUMN'S OWN FLOOR — narrower than the rail's, and deliberately.
 *
 * 300 is the narrowest a rail may be dragged, and while a stack of three was capped at ONE rail's
 * maximum those two numbers multiplied out to exactly the cap: three columns, each pinned at its
 * floor, with no slack anywhere. Every wall was immovable, which is a resize control that does not
 * resize. The cap is now per-column (see `stackCap`), and this floor is lower than the rail's, so
 * three columns at rest leave room on both sides of every wall. Below this a header is chevron,
 * title and icons with nothing to spare, which is the point at which a column stops being usable
 * and `fitLimit` stops offering one.
 */
const COL_MIN_W = 260;
/**
 * HOW WIDE THE WHOLE STACK MAY BE.
 *
 * RAIL_MAX_W is what ONE rail may be dragged to, and applying it to a stack of three was the bug:
 * the reader asked for three surfaces and got one rail's worth of room divided three ways. Each
 * column may reach the rail's own maximum, and what actually bounds the stack is the promise the
 * app makes about itself — MIN_CONTENT_W stays behind it, whatever is in front.
 *
 * For a single column this is exactly what it always was, which is why a plain rail is unchanged.
 */
const stackCap = (columns) => {
    const want = Math.max(1, columns) * RAIL_MAX_W;
    const room = (globalThis.innerWidth || 0) - MIN_CONTENT_W;
    return room > 0 ? Math.min(want, room) : want;
};
/* Light DOM (see createRenderRoot), so the styles ride with the markup rather than in a shadow sheet
   the app cannot reach. Rendered ONCE, by the panel, for every column it holds. */
export const panelCss = html `<style>
    /* The panel fills its rail and CONSTRAINS its children. Without min-height:0 a flex child
       refuses to shrink below its content, so an inner overflow:auto list grows the column instead
       of scrolling inside it — the list is then unscrollable and the rail just gets taller. */
    :is(vitesse-ai-panel, vitesse-ai-surface) {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-note {
      padding: 18px;
      font-size: 13px;
      line-height: 1.5;
      color: var(--text-secondary);
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-strip {
      padding: 8px 14px;
      font-size: 12px;
      color: var(--text-secondary);
      background: var(--bg-secondary);
      border-bottom: 1px solid var(--border-color);
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-strip-bad {
      color: var(--red);
    }
    /* The tree's own view switch. Sits at the top of the surface, not in the panel header, because
       it selects between renderings of THIS detour rather than between parts of the app. */
    /* The single chrome row. Nav on the left, the surface's own tabs beside it — see renderChrome
       for why these are one row and not two. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-chrome {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      flex-shrink: 0;
    }
    /* The tabs no longer own a row, so they contribute no padding of their own. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-chrome .ai-tabs {
      padding: 0;
    }
    /*
     * THE BASE PAGES' STICKY BAR, on this side of the divider.
     *
     * 52px including the border, the chevron at the left, the name, then the badges — the same row
     * every detail page in the product has. The border is what makes that TRUE rather than nearly
     * true: the page on the other side of the rail draws its rule at the bottom of its own 52px bar,
     * and without one here the two sides of one horizontal line stopped at the divider. Now they
     * meet.
     *
     * The chrome row supplies its own padding, so this cancels it and takes the height itself:
     * stepping between a thread and the tree must not move the content up and down.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      box-sizing: border-box;
      height: 52px;
      /* The chrome row's own padding, cancelled — and the width taken BACK. A 100% width resolves
         against the padded content box, so the row was 20px narrower than the panel and its rule
         stopped short of the right wall while the negative margin pulled the left edge out. The two
         together are what make the line span the whole header. */
      margin: -6px -10px;
      width: calc(100% + 20px);
      padding: 0 12px;
      flex-shrink: 0;
      color: var(--text-secondary);
      border-bottom: 1px solid var(--border-color);
    }
    /*
     * THE TRANSCRIPT SKELETON, restated for the LIGHT DOM.
     *
     * The rules themselves live beside the template in turn-view.ts, inside a shadow root the panel
     * does not have. Copied mechanically and never edited here, exactly as the head-lead rules above are,
     * and for the same reason: two surfaces show one moment — a thread arriving — and the pattern
     * that keeps them identical is one template with the styling stated wherever it has to render.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .skeleton {
      display: flex;
      flex-direction: column;
      gap: 18px;
      padding: 16px 12px;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .sk-row {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .sk-bar {
      height: 10px;
      border-radius: 5px;
      background: var(--vs-border, rgba(127, 127, 127, 0.25));
      animation: sk-pulse 1.4s ease-in-out infinite;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .sk-bar.short {
      width: 55%;
    }
    @keyframes sk-pulse {
      0%, 100% { opacity: 0.45; }
      50% { opacity: 0.8; }
    }
    @media (prefers-reduced-motion: reduce) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .sk-bar { animation: none; }
    }
    /* The badges sit beside the name, as they do on a page — measurements OF this surface, not
       controls, so they belong next to what they measure rather than pushed to the far end. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .base-status-badge {
      flex-shrink: 0;
    }
    /*
     * THE ACTIONS, HARD RIGHT — the arrangement every detail page in this product uses.
     *
     * auto margin rather than a spacer element, so the badges stay next to the name they measure
     * and everything after this is pushed to the wall.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
    }
    /* Two of the three are anchors, and an anchor has neither a button's line-height nor a
       button's lack of an underline. Restated where they now render, as the action row used to. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head-actions a.base-button {
      text-decoration: none;
      line-height: 1;
    }
    /*
     * THE SURFACE THAT SCROLLS — the rail's page, in the sense every detail page means it.
     *
     * The hub's list used to be a framed box scrolling inside a rail that did not move. One column
     * that scrolls, with the header stuck to the top of it, is what the rest of the product does,
     * and it is what makes a back-to-top control possible at all: there was previously no scrolling
     * container the header could be attached to.
     *
     * overscroll-behavior contains the rubber-band, so reaching the end of a long history does not
     * start scrolling the page behind the panel.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-surface-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      display: flex;
      flex-direction: column;
    }
    /*
     * THE HEADER STAYS. position: sticky on the row inside the chrome wrapper, which is where the
     * negative margins that align it live — sticking the wrapper instead would leave the row free
     * to scroll out from under it.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-surface-scroll .ai-chrome {
      position: sticky;
      top: 0;
      z-index: 2;
      background: var(--bg-primary);
    }
    /* The stamp on the bar's lip. Restated here rather than adopted, exactly as the lead's rules
       above are: base-page.scss nests this under .base-page-sticky-header, which this is not, and
       it defaults to opacity 0 because there it is revealed with a bar that fades in. Here the
       button is not rendered at all until the surface has moved, so it is simply visible. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head {
      position: relative;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .base-page-scroll-top {
      position: absolute;
      bottom: -11px;
      left: 50%;
      transform: translateX(-50%);
      width: 44px;
      height: 22px;
      padding: 0;
      border: 1px solid var(--border-color);
      border-radius: 7px;
      background-color: color-mix(in srgb, var(--bg-primary) 82%, transparent);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--text-secondary);
      z-index: 3;
      transition: color 0.15s ease;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .base-page-scroll-top:hover {
      color: var(--blue);
    }
    /*
     * AND THE LABELS COME OFF WHEN THERE IS NO ROOM FOR THEM.
     *
     * The reason these three lived on a row of their own was that a 360px rail cannot carry a
     * title, two badges and three labelled buttons — which is true, and is an argument about the
     * LABELS rather than about the row. A container query answers it directly: the header measures
     * itself, and below the width where the labels stop fitting they are dropped and the buttons
     * become their icons, which the base button already has a shape for.
     *
     * Keyed to the header, NOT the viewport: this rail is resized by dragging its divider, so a
     * media query would answer a question nobody asked about the window. The title keeps its 84px
     * floor either way, so the name is never the thing that disappears.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head {
      container-type: inline-size;
      container-name: ai-head;
    }
    @container ai-head (max-width: 460px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head-actions .base-button span {
        display: none;
      }
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head-actions .base-button {
        /* The icon-only shape: square-ish, and the gap has nothing left to separate. */
        padding: 0 8px;
        gap: 0;
      }
    }
    /* What is wrong with the document on screen, beside what it is. Shrinks with the badges. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .ai-head-bad {
      flex-shrink: 0;
      white-space: nowrap;
    }
    /* The host's editor fills the document pane, the way it fills a program view. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-doc {
      flex: 1;
      min-height: 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-doc > * {
      flex: 1;
      min-height: 0;
    }
    /*
     * WHAT AN AGENT MADE, SHOWN THE WAY A BOARD WOULD SHOW IT.
     *
     * The preview was dropped straight into the viewer's body with no rules at all: it sat against
     * the top-left corner, on the panel's own ground, with nothing around it — so the one question
     * the tab exists to answer ("does this look right on a board?") was being asked of something
     * that did not look like it was on a board.
     *
     * The frame is the tile treatment: the card ground, the border and the radius a widget gets
     * where it actually lives. Centred in what is left over, because a widget is smaller than a
     * column is tall and a thing pinned to the top of an empty panel reads as having fallen there.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-artifact {
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      overflow: auto;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-artifact > * {
      width: 100%;
      /* A widget is a tile, not a page: past this it stops looking like the thing being checked. */
      max-width: 560px;
      /* Tall enough to be a tile rather than a strip, short enough to sit inside a column. */
      min-height: 200px;
      max-height: 100%;
      display: block;
      background: var(--card-bg, var(--bg-secondary, var(--bg-primary)));
      border: 1px solid var(--border-color);
      border-radius: 8px;
      overflow: hidden;
    }
    /*
     * THE VIEWER'S PICKER, IN THE HEADER ROW RATHER THAN IN A BAND OF ITS OWN.
     *
     * It was a bordered bar inside the viewer's shadow root: a second header-shaped strip under the
     * one the surface already has, with the pills unstyled inside it — the pill bar is a LIGHT-DOM
     * element that takes its look from the app's global stylesheet, and a shadow root is where that
     * stylesheet stops. Here it is slotted content, so the app's own bar styles it, and it is the
     * same picker as every other page in the product.
     *
     * It gives way like the badges do: a viewer in a 260px column loses the labels before the title.
     */
    /*
     * THE VIEWER'S PICKER: A ROW OF PILLS ON THE PAGE, UNDER THE ONE HEADER THIS SURFACE HAS.
     *
     * No border, no ground of its own, no padding that makes it a band — anything that frames it
     * turns it into a second header, which is what it was when it lived inside the viewer's own
     * shadow root. Sideways, never DOWN: a bar that wraps in a 260px column is a bar that has grown
     * a second line nobody budgeted for.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-viewer-tabs {
      flex-shrink: 0;
      padding: 10px 12px 2px;
      min-width: 0;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      /* Its own container, so the pills below can be measured against the room THEY have. The row
         sits outside the header, so the header's container query cannot see it. */
      container-type: inline-size;
      container-name: viewer-tabs;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-viewer-tabs::-webkit-scrollbar {
      display: none;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-viewer-tabs .pill-tab-bar {
      flex-wrap: nowrap;
    }
    /*
     * THREE PILLS WITH WORDS ON THEM DO NOT FIT A 260px COLUMN — so they become their icons.
     *
     * Scrolling was the fallback and it is the wrong one here: a bar that scrolls hides the tab you
     * have not seen behind an edge with no sign it is there, so the third document simply did not
     * exist as far as the reader was concerned. Three icons fit with room to spare, and the label is
     * still on the title for anyone who hovers.
     *
     * Which is why each document carries its OWN icon (see artifactOf in places.ts): collapsed to
     * glyphs, two documents wearing the same page would be one control drawn twice.
     */
    @container viewer-tabs (max-width: 330px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-viewer-tabs .pill-tab-label {
        display: none;
      }
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-viewer-tabs .pill-tab {
        padding-left: 9px;
        padding-right: 9px;
      }
    }
    /*
     * THE MEASUREMENTS, IN ONE GROUP THAT MAY GIVE WAY — a thread's header already does this, and
     * this is that group restated for the LIGHT DOM the tree's header renders into.
     *
     * Loose badges with flex-shrink:0 do not overflow politely: they grow the row past its own box
     * and the column clips whatever is on the end, so the header simply stopped at the wall with
     * half a badge against it. A group that may shrink to nothing is what keeps the row inside the
     * column at all — the breakpoints below then decide what goes, rather than the divider doing it
     * with a pair of scissors.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics .badge {
      flex-shrink: 0;
    }
    /* Figures that do not jiggle as they climb: this one is counting up while the reader watches. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics .base-status-badge {
      font-variant-numeric: tabular-nums;
    }
    /* The wide spelling of the title is the one that shows until a breakpoint says otherwise. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-name .name-short {
      display: none;
    }
    /*
     * AND THE RESPONSIVE RULES GO LAST, which is not tidiness.
     *
     * A container query adds NO specificity — it only gates — so a breakpoint written above the
     * plain rule it means to override simply loses on source order and is dead. That is exactly how
     * the thread header's badge breakpoints were dead for a while; these come after what they
     * override, deliberately, and anything added later goes after them.
     */
    /* The unit words go before any badge does: "6" and "1.2M" still say what changed. */
    @container ai-head (max-width: 430px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics .u {
        display: none;
      }
    }
    /* Then the title sheds its first word — "Agent Tree" becomes "Tree", which is the same name one
       word shorter rather than a different one. */
    @container ai-head (max-width: 380px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-name .name-full {
        display: none;
      }
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-name .name-short {
        display: inline;
      }
    }
    /* Then whole badges, from the right — cost first, because it is the one the reader is least
       often watching change. */
    @container ai-head (max-width: 340px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics .badge:nth-child(n + 4) {
        display: none;
      }
    }
    @container ai-head (max-width: 300px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-metrics .badge:nth-child(n + 3) {
        display: none;
      }
    }
    /*
     * THE CONVERSATIONS RAIL HAS ITS OWN WIDTHS, because it carries three labelled actions that
     * the other headers do not. Measured in Chromium on Linux with the app's font stack, as the
     * header's content box, in English: everything fits from 562px, no unit words from 480px,
     * icons from 300px, one badge from 264px, no badges from 235px. Spanish needs about 60px more.
     * Each value below has a small margin above the English figure.
     *
     * Order: the unit words, then the labels, then the agents badge, then the count. The name
     * stays whole at the narrowest column. "New chat" never moves: in a language or a font that
     * needs more than these values, the metrics group shrinks first, and the actions stay.
     */
    @container ai-head (max-width: 600px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-hub-head .head-metrics .u {
        display: none;
      }
    }
    @container ai-head (max-width: 520px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-hub-head .ai-head-actions .base-button span {
        display: none;
      }
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-hub-head .ai-head-actions .base-button {
        padding: 0 8px;
        gap: 0;
      }
    }
    @container ai-head (max-width: 320px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-hub-head .head-metrics .badge:nth-child(n + 2) {
        display: none;
      }
    }
    @container ai-head (max-width: 280px) {
      :is(vitesse-ai-panel, vitesse-ai-surface) .ai-hub-head .head-metrics .badge {
        display: none;
      }
    }
    /*
     * The lead's own rules — the chevron and the name — live in head-lead.ts, and this is the LIGHT
     * DOM, so they are restated here against the panel's scope rather than adopted. Copied from that
     * one file mechanically and never edited here: the point of the shared template is that the
     * tree's header and a thread's are the same object, and a second set of values would put the
     * drift straight back.
     */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-back {
      margin-right: 4px; /* + the row's 8px = the base bar's 12px between chevron and name */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 0;
      background: transparent;
      cursor: pointer;
      color: var(--blue-light);
      font: inherit;
      flex-shrink: 0;
      transition: color 0.12s ease;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-back:hover {
      color: hsl(from var(--blue-light) h s calc(l - 10%));
    }
    /* The way home, restated for the light DOM exactly as the chevron above is. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-home {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-right: 2px;
      padding: 0;
      border: 0;
      background: transparent;
      cursor: pointer;
      color: var(--text-tertiary);
      font: inherit;
      flex-shrink: 0;
      transition: color 0.12s ease;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-home:hover {
      color: var(--blue-light);
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-head .head-name {
      flex: 0 1 auto;
      min-width: 84px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 15px;
      font-weight: 500;
      color: var(--text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-nav-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      border: 1px solid var(--border-color);
      border-radius: 7px;
      background: var(--bg-primary);
      color: var(--text-secondary);
      cursor: pointer;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-nav-btn:hover:not(:disabled) {
      color: var(--text-primary);
      border-color: var(--blue, var(--accent));
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-nav-btn:disabled {
      opacity: 0.35;
      cursor: default;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-tabs {
      display: flex;
      gap: 6px;
      padding: 8px 10px;
      flex-shrink: 0;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-tabs button {
      font: inherit;
      font-size: 12px;
      cursor: pointer;
      padding: 5px 12px;
      border-radius: 999px;
      border: 1px solid var(--border-color);
      background: var(--bg-primary);
      color: var(--text-secondary);
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-tabs button:hover {
      color: var(--text-primary);
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-tabs button.on {
      background: var(--blue, var(--accent));
      border-color: var(--blue, var(--accent));
      color: #fff;
    }
    /* The canvas takes the room that is left; without a height it collapses to its min and the
       graph is drawn into a letterbox. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-stage {
      flex: 1;
      min-height: 0;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-back {
      display: block;
      margin-top: 10px;
      font: inherit;
      cursor: pointer;
      background: none;
      border: 0;
      padding: 0;
      color: var(--blue, var(--accent));
    }
    /* ask_user — MINIMAL wiring styling only. A functional card pinned above the composer while the
       AI waits on an answer; the real treatment (sit it in the thread like a message) is a later
       pass. Enough here that it is usable and not broken. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask {
      box-sizing: border-box;
      max-width: 100%;
      flex-shrink: 0;
      margin: 8px 12px;
      padding: 12px;
      border: 1px solid var(--blue, var(--accent));
      border-radius: 12px;
      background: var(--bg-secondary);
      display: flex;
      flex-direction: column;
      gap: 8px;
      overflow: hidden;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-who {
      font-size: 11.5px;
      color: var(--text-secondary);
      margin-bottom: 4px;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-q {
      font-size: 13.5px;
      color: var(--text-primary);
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
    /* One suggestion per row, full width, LEFT-aligned, wrapping — never running off the right edge
       under the app chrome. The free-text reply below is always there, so a person is never limited
       to these. */
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-opts {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-opt {
      width: 100%;
      box-sizing: border-box;
      text-align: left;
      padding: 8px 10px;
      font: inherit;
      font-size: 13px;
      color: var(--text-primary);
      background: var(--bg-primary);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      cursor: pointer;
      overflow-wrap: anywhere;
      transition:
        border-color 0.13s ease,
        color 0.13s ease;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-opt:hover {
      border-color: var(--blue, var(--accent));
      color: var(--blue, var(--accent));
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-reply {
      display: flex;
      gap: 6px;
      align-items: center;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-input {
      flex: 1;
      min-width: 0;
      padding: 7px 10px;
      font: inherit;
      font-size: 13px;
      color: var(--text-primary);
      background: var(--bg-primary);
      border: 1px solid var(--border-color);
      border-radius: 8px;
    }
    :is(vitesse-ai-panel, vitesse-ai-surface) .ai-ask-input:focus {
      outline: none;
      border-color: var(--blue, var(--accent));
    }
    /* THE STACK: the trail's window as columns, newest on the LEFT, anchored to the right edge so a
       column arriving reveals itself where the rail widened and nothing already on screen moves. */
    vitesse-ai-panel .ai-stack {
      flex: 1;
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      min-height: 0;
      min-width: 0;
    }
    vitesse-ai-surface {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }
  </style>`;
/** The most columns the stack will ever draw. Three is what fits at the rail's own minimum width on
 *  a laptop screen; past that a column is too narrow to read a thread in. */
export const MAX_COLUMNS = 3;
/**
 * Where each column's width persists.
 *
 * The FIRST column uses the rail's own key, because a stack of one column IS the rail: a reader who
 * dragged the assistant to 520px last week must find it at 520px the first time the stack draws it,
 * not back at the default.
 */
const columnKey = (i) => (i === 0 ? RAIL_STORAGE_KEY : `vitesse.ai.colWidth.${i}`);
/** How many columns fit beside the app at their narrowest. At least one, always: a stack of one
 *  clipped column is still better than no panel, and the rail's own clamp keeps it honest. */
const fitLimit = () => {
    // The room beside the app, NOT one rail's maximum: a stack of three is three rails, and measuring
    // it against the width of one is what pinned every column to its floor with nowhere to drag.
    const room = (globalThis.innerWidth || 0) - MIN_CONTENT_W;
    const usable = room > 0 ? room : RAIL_MAX_W;
    return Math.max(1, Math.min(MAX_COLUMNS, Math.floor(usable / COL_MIN_W)));
};
let VitesseAiPanel = class VitesseAiPanel extends LitElement {
    constructor() {
        super(...arguments);
        /** The signed-in person's first name, for the composer's greeting. Absent is fine. */
        this.userName = '';
        /*
         * ONE TRAIL ACROSS EVERY SURFACE, including between chats.
         *
         * The arithmetic lives in `nav-trail.ts` — truncate-on-visit, the bounds at each end, the window
         * the columns draw, immutable so it drops into reactive state without manual invalidation. Small
         * enough to look obvious and easy enough to get subtly wrong, which is why it is a tested module
         * and not a few lines inlined here.
         */
        this.trail = emptyTrail();
        /** True when the reader has stepped back from the composer to the hub. */
        this.atHub = false;
        /*
         * HOW MANY COLUMNS ARE DRAWN — and the whole rule in one line:
         *
         *     columns shown = window(trail, fits)
         *
         * THERE IS NO SECOND NUMBER. There was: a remembered "budget" the reader could lower by closing a
         * column and raise with a widen control on the wall. It was invisible, it persisted, and it
         * silently governed everything opened afterwards — close one column and every later click put its
         * result on the left and pushed the oldest off, leaving a third of the rail empty with no sign of
         * why. A hidden preference that changes what a click does is a worse control than no control.
         *
         * So the stack simply draws as many of the most recent places as the window can hold. Closing a
         * column removes THAT PLACE from the trail, which is the whole of what closing means.
         *
         * The rail clamps its width to the room the app keeps, and the columns inside it do not: three
         * columns at their minimum are 900px, and on a 1200px laptop with the tree open that is wider than
         * the rail is allowed to be. The overflow does not scroll, it CLIPS — so the leftmost column, the
         * one being worked in, would be the half cut off.
         *
         * So the stack draws what fits. Make the window bigger and a third column appears on its own;
         * make it smaller and the reader loses a column of CONTEXT rather than half of the column they
         * are working in.
         */
        this.fits = fitLimit();
        this.onChange = () => this.requestUpdate();
        this.onResize = () => {
            const n = fitLimit();
            if (n !== this.fits)
                this.fits = n;
        };
        /**
         * Reopen where the person left off.
         *
         * Once per manager, and only into the composer's place — if a place is already on the trail (the
         * host asked for one, or a link named one) that is a deliberate destination and must win. A thread
         * that has since been deleted simply fails to open, which lands at the composer: the same place as
         * having nothing remembered.
         */
        this.resumed = false;
        /** Out to the list, from any column: the trail is emptied, the hub shown. */
        this.leave = () => {
            if (this.manager)
                rememberConversation(this.manager.surface, '');
            this.manager?.close();
            this.trail = emptyTrail();
            this.atHub = true;
        };
        /**
         * A NEW THREAD, from the composer.
         *
         * Everything the composer collected travels with the message — the model, the effort, the
         * specialist and the files. The thread is opened here so those can be set on it before the first
         * turn; the column that shows it opens the same id a frame later and is handed the same object
         * (see `AiManager.open`), so nothing set here is lost on the way.
         */
        this.startNew = async (sub) => {
            if (!this.manager)
                return;
            const conv = await this.manager.open(undefined, { model: sub.chatModel });
            if (sub.effort)
                conv.effort = sub.effort;
            if (sub.expertId !== undefined)
                conv.expertId = sub.expertId;
            this.visit({ k: 'chat', id: conv.id });
            await conv.send(sub.text, 'user', '', sub.files.map((f) => f.file), sub.references);
        };
        this.announced = 0;
        this.announcedDrag = false;
        /*
         * THE COLUMNS ARE BROUGHT INSIDE THE CAP, rather than left to the flex row to squeeze.
         *
         * Shrinking is the safety net — nobody is ever clipped — but it is a bad thing to live in. Each
         * column remembers its own width, and two columns restored from a wider day sum past what the rail
         * may take up; the row then scales all of them, so a drag of 120px moved the divider 53px and the
         * boundary no longer followed the pointer. A divider that does not track the cursor reads as a
         * broken control, which is the one thing a resize must never do.
         *
         * So the excess is taken here, once, proportionally to each column's HEADROOM above its own floor
         * — a column already at 300 gives nothing — after which the widths are the widths and every drag
         * is exact. NOT persisted: this is a correction for the room available now, and the reader's
         * dragged widths come back the moment there is room for them again.
         */
        this.normalising = false;
        this.surfaceScrolled = false;
        this.surfaceRaf = 0;
        this.onSurfaceScroll = (e) => {
            if (this.surfaceRaf)
                return;
            const el = e.currentTarget;
            this.surfaceRaf = requestAnimationFrame(() => {
                this.surfaceRaf = 0;
                this.surfaceScrolled = el.scrollTop > 0;
            });
        };
    }
    createRenderRoot() {
        return this;
    }
    willUpdate(changed) {
        if (changed.has('manager')) {
            const prev = changed.get('manager');
            prev?.removeEventListener('change', this.onChange);
            this.manager?.addEventListener('change', this.onChange);
            prev?.conversations?.removeEventListener('change', this.onChange);
            this.manager?.conversations.addEventListener('change', this.onChange);
            this.resume();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        globalThis.addEventListener?.('resize', this.onResize);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        globalThis.removeEventListener?.('resize', this.onResize);
        this.manager?.removeEventListener('change', this.onChange);
        this.manager?.conversations.removeEventListener('change', this.onChange);
    }
    resume() {
        if (this.resumed || !this.manager || currentPlace(this.trail))
            return;
        this.resumed = true;
        const id = lastConversation(this.manager.surface);
        if (id)
            this.visit({ k: 'chat', id });
    }
    /* ── THE TRAIL ─────────────────────────────────────────────────────────────────────────────── */
    /** The one chrome row — see the surface's `renderChrome` for why nav and a view's own tabs share
     *  a row rather than stacking two on a rail that is already short of height. */
    renderChrome(tabs) {
        return html `<div class="ai-chrome">${tabs}</div>`;
    }
    /**
     * Go somewhere NEW: records it and discards any forward branch, as a browser does.
     *
     * Somewhere you already ARE is not somewhere new. Without that guard the place you are standing
     * in is pushed on top of itself — two columns of the same thread, and a back that appears not to
     * move because it lands on the same place again.
     */
    visit(p) {
        this.atHub = false;
        const here = currentPlace(this.trail);
        if (here && placeKey(here) === placeKey(p)) {
            /*
             * THE SAME DESTINATION, POSSIBLY A DIFFERENT WAY IN.
             *
             * A canvas is keyed by the tree it draws, so opening it from a second agent is the same place
             * — but not the same arrival: it must re-centre on the card the reader just stepped out of,
             * not the one they came from last time. Rewritten in place rather than pushed, so the stack
             * does not draw the same tree in two columns.
             */
            if (JSON.stringify(here) !== JSON.stringify(p))
                this.trail = replacePlace(this.trail, p);
            return;
        }
        /*
         * A PLACE ALREADY BEHIND THE READER MOVES TO THE FRONT — it is not copied.
         *
         * The trail IS what is on screen here, so a plain history push would draw the same thread in two
         * columns: two live surfaces over one conversation, two composers writing to it, and a duplicate
         * key in the column list besides. Opening something you can already see two columns along brings
         * it to hand instead.
         */
        /*
         * A DETOUR DOES NOT BECOME A LANDMARK.
         *
         * The canvas is somewhere you go to get your bearings and then leave — and left on the trail it
         * came back: three columns of the most recent places, one of them a tree opened twenty minutes
         * ago, appearing in the middle of the stack with nobody having asked for it. So stepping off a
         * canvas REPLACES it rather than stacking on top of it. It is one click away in every thread's
         * header, which is the right cost for a thing you want on purpose and never by accident.
         */
        // The canvas took a column over; stepping off it hands that column back to the thread it
        // replaced, and the new place opens beside them both. Without the restore the thread would be
        // lost from the trail altogether and back from the agent would skip over it.
        if (here?.k === 'tree' && p.k !== 'tree' && here.origin)
            this.trail = replacePlace(this.trail, here.origin);
        this.trail = visitOrdered(this.trail, p, (a, b) => placeKey(a) === placeKey(b), (x) => this.depthOf(x));
        if (p.k === 'chat' && this.manager)
            rememberConversation(this.manager.surface, p.id);
    }
    /**
     * HOW FAR DOWN THE HIERARCHY A PLACE SITS, so the columns read as the hierarchy they are.
     *
     * The stack is right-to-left: the thread that started the work on the right, what it spawned to
     * its left. A place with no run behind it — a person's own thread — is the top, and a run the tree
     * has not loaded answers the same, which is honest: an unknown place goes where a root goes rather
     * than being asserted into the middle of somebody else's family.
     */
    depthOf(p) {
        const runId = p.k === 'run' || p.k === 'viewer' ? p.runId : p.k === 'tree' ? p.root : '';
        if (!runId)
            return 0;
        return Math.max(0, this.manager?.runTree?.depthOf(runId) ?? 0);
    }
    /** Back is stepping out of the left column: the place is dropped, so the columns close up. */
    back() {
        const t = popTrail(this.trail);
        if (t === this.trail)
            return;
        this.trail = t;
        if (!currentPlace(t))
            this.atHub = true;
    }
    /**
     * Close a CONTEXT column — one step of history behind the current place.
     *
     * The place leaves the trail. That is all of it, and it used to be half: closing also lowered a
     * remembered column count, which then governed every later click — see the note on `fits`. If
     * there is more history behind it, the next place back takes the slot, which is what a stack of
     * the most recent places means.
     */
    closeColumn(index) {
        this.trail = closeAtTrail(this.trail, this.trail.at - index);
    }
    /**
     * The name of the place BACK leads to, for the left column.
     *
     * Read off the trail rather than hard-coded, because that is what back actually does: it walks one
     * step down the trail, and from an agent's canvas that step is usually the thread it was opened
     * from. A button labelled "Conversations" that lands on a conversation is a small lie.
     */
    previousLabel() {
        const prev = this.trail.items[this.trail.at - 1];
        if (!prev)
            return t('ai.backToConversations');
        if (prev.k === 'hub')
            return t('ai.backToConversations');
        if (prev.k === 'tree')
            return t('ai.pipes');
        // A viewer is named by what it shows, not by the thread the agent behind it talked in.
        if (prev.k === 'viewer') {
            const made = artifactOf(this.manager?.runTree?.run(prev.runId)?.ret?.deliverableJson ?? '');
            if (made)
                return made.title || t('ai.viewerRender');
        }
        const id = prev.k === 'chat' ? prev.id : this.manager?.runTree?.run(prev.runId)?.conversationId;
        const c = id ? this.manager?.conversations.conversations.find((x) => x.id === id) : undefined;
        if (prev.k === 'run') {
            const run = this.manager?.runTree?.run(prev.runId);
            if (run)
                return runLabel(run);
        }
        return conversationLabel({ title: c?.title ?? '', preview: c?.preview ?? '' });
    }
    /**
     * What a column may ask of this panel.
     *
     * The LEFT column is the place being worked in: its back is the trail's back and its "previous"
     * is the trail's previous. A column to the right is history: opening something from it still
     * pushes on top (the reader is going deeper from the bigger picture), but its back CLOSES it —
     * there is nothing further back for a context column to show that is not already beside it.
     */
    navFor(index) {
        const trailIndex = this.trail.at - index;
        const left = index === 0;
        return {
            visit: (p) => this.visit(p),
            replaceCurrent: (p) => {
                if (left)
                    this.trail = replacePlace(this.trail, p);
                else
                    this.trail = { items: this.trail.items.map((x, i) => (i === trailIndex ? p : x)), at: this.trail.at };
            },
            /*
             * BACK on the left column is navigation — step out of where you are, and if the trail runs
             * deeper the stack stays as wide as it was. On a CONTEXT column the same control is a close:
             * there is nothing further back for it to show that is not already sitting beside it.
             */
            back: () => (left ? this.back() : this.closeColumn(index)),
            canBack: left ? canBackTrail(this.trail) : true,
            previousLabel: left ? this.previousLabel() : t('ai.closeColumn'),
            // What a `visit` from this surface will look like: a column beside it, or this column
            // becoming somewhere else. The room is the only thing that decides.
            stacks: this.fits > 1,
            leave: this.leave,
            // The list with nothing open is the hub; the same with the hub put away is the composer.
            startNew: () => {
                this.leave();
                this.atHub = false;
            },
        };
    }
    /* ── OPENING THINGS ────────────────────────────────────────────────────────────────────────── */
    /**
     * A CLICK IN THE LIST OPENS THE THING CLICKED. Immediately.
     *
     * A run place rather than a chat place for an agent's row, so its thread opens with its own header
     * — state, elapsed, cost — instead of as an anonymous conversation. Falls back to the thread when
     * the row has no run behind it, which is every agent recorded before the run id travelled with it.
     */
    openFromList(id, row) {
        if (row?.depth && row.runId) {
            this.visit({ k: 'run', runId: row.runId });
            return;
        }
        this.visit({ k: 'chat', id });
    }
    /* ── RENDERING ─────────────────────────────────────────────────────────────────────────────── */
    /** The columns on screen right now: the trail's window, newest first. */
    columns() {
        return windowTrail(this.trail, this.fits).filter((p) => p.k !== 'hub');
    }
    /**
     * TELL THE RAIL HOW WIDE TO BE — the sum of what the columns are actually at.
     *
     * Read off the elements rather than tracked in parallel, because they are the owners: each column
     * remembers its own width and restores it on mount, so a second copy here would be wrong for
     * exactly one frame on every open, and that is the frame the reader sees.
     *
     * `null` when there are no columns (the hub, the composer): the rail then keeps its own width and
     * its own wall, exactly as it did before the stack existed.
     */
    /** The nth column on screen, for a column that needs to reach the one it shares a wall with. */
    columnAt(i) {
        return this.querySelectorAll('vitesse-resizable-column')[i];
    }
    /** How wide the outermost column may be drawn: the cap, less everything beside it. */
    outerCeiling() {
        const cols = [...this.querySelectorAll('vitesse-resizable-column')];
        const rest = cols.slice(1).reduce((n, c) => n + c.width, 0);
        return Math.max(COL_MIN_W, stackCap(cols.length) - rest);
    }
    reportWidth() {
        const cols = [...this.querySelectorAll('vitesse-resizable-column')];
        this.fitToCap(cols);
        const width = cols.length ? cols.reduce((n, c) => n + c.width, 0) : null;
        /*
         * AND WHETHER A WALL IS MOVING, because only the panel can see it.
         *
         * The rail has to take its width transition off for the duration, and the columns doing the
         * dragging are in the panel's light DOM, not the rail's. Announced alongside the width so the
         * two arrive together: a rail told the width but not the gesture animates towards it over 220ms
         * while the columns are already there, and clips whatever it has not reached.
         */
        const dragging = cols.some((c) => c.resizing);
        if (width === this.announced && dragging === this.announcedDrag)
            return;
        this.announced = width;
        this.announcedDrag = dragging;
        this.dispatchEvent(new CustomEvent('vitesse-ai-columns', { detail: { width, dragging }, bubbles: true, composed: true }));
    }
    fitToCap(cols) {
        // NOT WHILE A WALL IS BEING DRAGGED. The correction below reads what each column was last
        // dragged to, which mid-gesture is where it was BEFORE this one started — so running it on the
        // resize events the drag itself fires would cancel every frame of it. Observed exactly that way:
        // the wall simply would not move.
        if (this.normalising || !cols.length || cols.some((c) => c.resizing))
            return;
        const cap = stackCap(cols.length);
        /*
         * FROM WHAT THEY WERE DRAGGED TO, every time — not from wherever the last correction left them.
         *
         * Otherwise the squeeze is one-way: three columns are brought down to fit, the reader closes one
         * and the other two stay at the width they were squeezed into, with a third of the rail empty
         * beside them. Recomputing from the dragged widths makes this idempotent, so a column closing is
         * its neighbours springing back to what the reader actually asked for.
         */
        const want = cols.map((c) => c.desiredWidth);
        const excess = want.reduce((a, b) => a + b, 0) - cap;
        const headroom = want.map((w) => Math.max(0, w - COL_MIN_W));
        const total = headroom.reduce((a, b) => a + b, 0);
        // Every column already at its floor: nothing to give, and the fit limit drops one instead.
        const next = excess > 0 && total > 0 ? want.map((w, i) => w - (excess * headroom[i]) / total) : want;
        if (next.every((w, i) => Math.abs(w - cols[i].width) < 1))
            return;
        this.normalising = true;
        cols.forEach((c, i) => c.setWidth(next[i], false));
        this.normalising = false;
    }
    updated() {
        this.reportWidth();
    }
    render() {
        const s = this.manager?.current;
        const cols = this.columns();
        /*
         * THE DOORS ARE ALWAYS OPEN. Connection trouble is a strip above them, not a wall in front of
         * them: the recents list reads the archive and starting a thread is a local act. Only SENDING
         * needs the plane.
         */
        return html `${panelCss} ${this.renderStatus(s)}
    ${cols.length
            ? this.renderColumns(cols)
            : this.atHub
                ? this.renderHub()
                : this.renderComposer()}`;
    }
    renderStatus(s) {
        if (s?.connState === 'error')
            return html `<div class="ai-strip ai-strip-bad">${s.tokenError || t('ai.unreachable')}</div>`;
        return nothing;
    }
    /**
     * THE COLUMNS. Keyed by place, so a column keeps its element — and its scroll, its stream, its
     * open disclosures — while columns arrive and leave beside it. Newest first in the DOM, which is
     * leftmost on screen.
     */
    renderColumns(cols) {
        return html `<div class="ai-stack" @column-resize=${() => this.reportWidth()}>
      ${repeat(cols, (p) => placeKey(p), (p, i) => html `<vitesse-resizable-column
          storage-key=${columnKey(i)}
          min-width=${COL_MIN_W}
          max-width=${RAIL_MAX_W}
          .ceiling=${
        /*
         * THE OUTERMOST WALL STOPS AT THE CAP, rather than overshooting and being pulled back.
         *
         * It is the one wall that makes the whole stack wider, and a column's own maximum knows
         * nothing about the columns beside it — so a drag could take the stack past what the
         * room allows, and the correction at release yanked it home in one frame. A ceiling that
         * accounts for its neighbours means the divider simply stops where the stack stops.
         *
         * Inner walls need none: a shared wall moves a boundary, so the total cannot grow.
         */
        i === 0 ? () => this.outerCeiling() : undefined}
          .sharesWallWith=${
        // The OUTERMOST wall is the rail's own edge: dragging it makes the stack wider, which is
        // what the rail has always done. Every wall inside it has a column on each side, so
        // moving it takes from one and gives to the other and the stack stays as wide as it was
        // — the symbol grid's dividers, and what anyone who has dragged one expects.
        i === 0 ? undefined : () => this.columnAt(i - 1)}
        >
          <vitesse-ai-surface
            .manager=${this.manager}
            .userName=${this.userName}
            .place=${p}
            .nav=${this.navFor(i)}
            density=${i === 0 ? 'full' : 'context'}
          ></vitesse-ai-surface>
        </vitesse-resizable-column>`)}
    </div>`;
    }
    renderComposer() {
        const s = this.manager?.current;
        return html `<vitesse-ai-composer
      surface=${this.manager?.surface ?? ''}
      .userName=${this.userName}
      .specialists=${this.manager?.specialists ?? []}
      .defaultChatModel=${s?.textModel ?? ''}
      .defaultVoiceModel=${s?.model ?? ''}
      .defaultImageModel=${s?.imageModel ?? ''}
      .onSubmit=${this.startNew}
      .onStartVoice=${() => this.manager?.dispatchEvent(new CustomEvent('start-voice'))}
      .onBack=${() => (this.atHub = true)}
      .onBackHover=${() => this.manager?.conversations.prefetch()}
    ></vitesse-ai-composer>`;
    }
    backToTop() {
        this.querySelector('.ai-surface-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
    renderHub() {
        const store = this.manager?.conversations;
        const threads = store?.conversations.length ?? 0;
        const agents = (store?.conversations ?? []).reduce((n, c) => n + (c.agents?.length ?? 0), 0);
        const consoleUrl = this.manager?.consoleUrl ?? '';
        /*
         * MEMORIES AND THE CONSOLE ARE LINKS, not buttons: they open the console in another tab, and
         * middle-click has always worked on them — rendered through `linkButton` so that stays true.
         */
        const head = html `<div class="ai-head ai-hub-head">
      ${headLead({ backLabel: '', title: t('ai.hubRecents'), icon: 'message-circle' })}
      <!-- THE COUNTS, IN THE GROUP THAT GIVES WAY — the agent tree's header does the same. They
           were loose badges that refused to shrink, so a rail too narrow for the labels pushed
           the actions past the wall and "New chat" was cut off. The group shrinks to nothing
           before any action moves; the container queries then decide what goes. -->
      <div class="head-metrics">
        ${threads
            ? html `<span class="badge" title=${t('ai.threadCount', { count: threads })}
              >${statusBadge('blue', badgeMetric(String(threads), t('ai.threadsUnit', { count: threads })))}</span
            >`
            : nothing}
        ${agents
            ? html `<span class="badge" title=${t('ai.subagentCount', { count: agents })}
              >${statusBadge('green', badgeMetric(String(agents), t('ai.agentsUnit', { count: agents })))}</span
            >`
            : nothing}
      </div>
      <div class="ai-head-actions">
        ${consoleUrl
            ? linkButton({ label: t('ai.hubMemories'), title: t('ai.hubMemories'), icon: 'brain', size: 'sm', href: `${consoleUrl}/#memory` })
            : nothing}
        ${consoleUrl
            ? linkButton({ label: t('ai.hubConsole'), title: t('ai.hubConsole'), icon: 'chart-no-axes-combined', size: 'sm', href: `${consoleUrl}/` })
            : nothing}
        ${button({ label: t('ai.newChat'), title: t('ai.newChat'), icon: 'plus', size: 'sm', onClick: () => (this.atHub = false) })}
      </div>
      ${this.surfaceScrolled
            ? html `<button
            class="base-page-scroll-top"
            @click=${() => this.backToTop()}
            title=${t('common.backToTop')}
            aria-label=${t('common.backToTop')}
          >
            <vitesse-icon name="chevron-up" size="15"></vitesse-icon>
          </button>`
            : nothing}
    </div>`;
        return html `<div class="ai-surface-scroll" @scroll=${this.onSurfaceScroll}>
      ${this.renderChrome(head)}
      <vitesse-ai-hub .store=${store} .onPick=${(id, row) => this.openFromList(id, row)}></vitesse-ai-hub>
    </div>`;
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiPanel.prototype, "manager", void 0);
__decorate([
    property()
], VitesseAiPanel.prototype, "userName", void 0);
__decorate([
    state()
], VitesseAiPanel.prototype, "trail", void 0);
__decorate([
    state()
], VitesseAiPanel.prototype, "atHub", void 0);
__decorate([
    state()
], VitesseAiPanel.prototype, "fits", void 0);
__decorate([
    state()
], VitesseAiPanel.prototype, "surfaceScrolled", void 0);
VitesseAiPanel = __decorate([
    customElement('vitesse-ai-panel')
], VitesseAiPanel);
export { VitesseAiPanel };
//# sourceMappingURL=panel.js.map